// Stempeluhr — Kommen und Gehen.

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import {
  inWarteschlange,
  warteschlangeLesen,
  warteschlangeSenden,
} from "@/lib/stempel-warteschlange";
import { useAuth } from "@/stores/authStore";

export interface TimeEntry {
  id: string;
  profile_id: string;
  started_at: string;
  ended_at: string | null;
  break_minutes: number;
  note: string | null;
  /** Liegt noch auf dem Gerät und wurde noch nicht übertragen. */
  nochNichtUebertragen?: boolean;
}

/** Läuft gerade eine Schicht? Steuert den großen Knopf und den Punkt auf der Kachel. */
export function useLaufendeSchicht() {
  return useQuery({
    queryKey: ["zeit-laufend"],
    // Auch hier "always": Sonst friert die Abfrage im Funkloch ein und die Seite
    // erfährt nie, dass auf dem Gerät ein Stempel wartet.
    networkMode: "always",
    queryFn: async () => {
      const me = useAuth.getState().user?.id;
      if (!me) return null;

      // Anstossen, aber NICHT darauf warten. Im Funkloch hängt die Netzanfrage
      // bis zum Zeitablauf — und solange stünde hier "nicht eingestempelt",
      // obwohl der Stempel längst auf dem Gerät liegt. Gemessen: genau daran
      // blieb der Knopf hängen.
      void warteschlangeSenden();

      // ══ Ohne Netz zählt der Stempel auf dem Gerät ══
      //
      // Sonst steht dort "nicht eingestempelt", der Mitarbeiter tippt ein
      // zweites Mal, und am Ende des Tages streiten sich zwei Schichten. Was
      // festgeschrieben ist, gilt — auch wenn der Server es noch nicht kennt.
      const wartend = warteschlangeLesen().find((e) => e.profile_id === me);
      const vomGeraet = () =>
        ({
          id: wartend!.id,
          profile_id: me,
          started_at: wartend!.started_at,
          ended_at: null,
          break_minutes: 0,
          note: null,
          nochNichtUebertragen: true,
        }) as TimeEntry;

      // Ohne Netz WIRFT supabase-js (TypeError: Failed to fetch), es gibt kein
      // `error`-Objekt zurück. Ohne dieses try/catch stirbt die Abfrage, der
      // Knopf fällt auf "Einstempeln" zurück — und der Mitarbeiter glaubt, sein
      // Stempel sei weg. Gemessen: genau so trat es im Funkloch auf.
      let data: TimeEntry | null = null;
      try {
        const antwort = await supabase
          .from("time_entries")
          .select("*")
          .eq("profile_id", me)
          .is("ended_at", null)
          .maybeSingle();
        if (antwort.error) {
          if (wartend) return vomGeraet();
          throw antwort.error;
        }
        data = (antwort.data as TimeEntry) ?? null;
      } catch (e) {
        if (wartend) return vomGeraet();
        throw e;
      }

      if (data) return data;
      return wartend ? vomGeraet() : null;
    },
    refetchOnWindowFocus: true,
  });
}

/** Die eigenen Einträge, neueste zuerst. */
export function useMeineZeiten(limit = 60) {
  return useQuery({
    queryKey: ["zeit-liste", limit],
    queryFn: async () => {
      const { data: session } = await supabase.auth.getSession();
      const me = session.session?.user.id;
      if (!me) return [];

      const { data, error } = await supabase
        .from("time_entries")
        .select("*")
        .eq("profile_id", me)
        .order("started_at", { ascending: false })
        .limit(limit);
      if (error) throw error;
      return (data ?? []) as TimeEntry[];
    },
  });
}

export function useEinstempeln() {
  const qc = useQueryClient();
  return useMutation({
    // ══ Der eigentliche Grund, warum Stempel verschwanden ══
    //
    // TanStack Query PAUSIERT Mutationen, sobald der Browser sich für offline
    // hält (Standard: networkMode "online"). Die Funktion unten wird dann gar
    // nicht erst aufgerufen — es gibt keinen Eintrag, keinen Fehler, keine
    // Meldung. Der Knopf sieht aus wie immer, und die Arbeitszeit ist weg.
    //
    // Gemessen im Funkloch: localStorage blieb leer, die Datenbank leer, die
    // Oberfläche stumm. Genau das hat Fabian auf dem iPhone erlebt.
    //
    // "always" heisst: immer ausführen. Das Schreiben auf das Gerät braucht kein
    // Netz, und der Versand darf scheitern — dafür ist die Warteschlange da.
    networkMode: "always",
    mutationFn: async () => {
      // Die Kennung kommt aus dem BEREITS geladenen Zustand, nicht über
      // supabase.auth.getSession(). Der Aufruf ist asynchron und kann im
      // Funkloch hängen oder scheitern — dann käme der Stempel nie bis zur
      // Warteschlange, und genau das soll hier ja nicht mehr passieren.
      // (Gemessen: ohne diese Änderung blieb die Warteschlange offline leer.)
      const me = useAuth.getState().user?.id;
      if (!me) throw new Error("Nicht angemeldet");

      // ══ Erst festschreiben, dann senden ══
      //
      // Auf dem iPhone bricht das Betriebssystem laufende Anfragen ab, sobald
      // die App in den Hintergrund geht. Wer tippt und sofort wegwischt, dessen
      // Stempel starb bisher auf halbem Weg — und Arbeitszeit ist Lohn.
      //
      // Der Eintrag landet deshalb ZUERST auf dem Gerät. Dieser Schritt kann
      // nicht scheitern. Erst danach wird gesendet; klappt es nicht, bleibt er
      // liegen und geht beim nächsten Öffnen raus.
      const eintrag = {
        id: crypto.randomUUID(),
        profile_id: me,
        started_at: new Date().toISOString(),
        versuche: 0,
      };
      inWarteschlange(eintrag);

      // Die Anzeige SOFORT umstellen, ohne den Umweg über ein Neuladen der
      // Abfrage. Im Funkloch ist dieser Umweg unzuverlässig, und der Knopf blieb
      // dann auf "Einstempeln" stehen — worauf der Mitarbeiter ein zweites Mal
      // tippt. Was auf dem Gerät steht, gilt; das wird hier direkt gesetzt.
      qc.setQueryData(["zeit-laufend"], {
        id: eintrag.id,
        profile_id: me,
        started_at: eintrag.started_at,
        ended_at: null,
        break_minutes: 0,
        note: null,
        nochNichtUebertragen: true,
      } as TimeEntry);

      // Bewusst OHNE await: Der Stempel ist mit der Zeile darüber gesichert.
      // Würde hier auf den Versand gewartet, hinge die Oberfläche im Funkloch am
      // Zeitablauf der Netzanfrage.
      void warteschlangeSenden();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["zeit-laufend"] });
      qc.invalidateQueries({ queryKey: ["zeit-liste"] });
    },
  });
}

export function useAusstempeln() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, pause }: { id: string; pause: number }) => {
      const { error } = await supabase
        .from("time_entries")
        .update({ ended_at: new Date().toISOString(), break_minutes: pause })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["zeit-laufend"] });
      qc.invalidateQueries({ queryKey: ["zeit-liste"] });
    },
  });
}

/**
 * Die Zeiten EINES Menschen — für die Leitung, im Profil des Mitarbeiters.
 *
 * Sebastian will nicht in einer Liste aller Zeiten suchen, sondern beim
 * Mitarbeiter nachsehen: Ist er gerade da? Wie viel diese Woche? Was war letzte
 * Woche? Deshalb liegt es dort, wo er ohnehin hinschaut, wenn es um die Person
 * geht — und nicht nur in der Sammelansicht.
 *
 * Stornierte Einträge kommen MIT: Sie stehen durchgestrichen da. Eine Korrektur
 * verschwinden zu lassen wäre genau das, was die Revisionssicherheit verhindern
 * soll.
 */
export function useZeitenVon(profileId: string | null, limit = 40) {
  return useQuery({
    queryKey: ["zeit-person", profileId, limit],
    enabled: !!profileId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("time_entries")
        .select("*")
        .eq("profile_id", profileId!)
        .order("started_at", { ascending: false })
        .limit(limit);
      if (error) throw error;
      return (data ?? []) as TimeEntry[];
    },
  });
}

export interface TimeEntryMitPerson extends TimeEntry {
  person?: { full_name: string | null; role: string } | null;
}

/**
 * Zeiten ALLER Mitarbeiter — nur für die Leitung.
 *
 * Die RLS lässt admin/buero alles sehen, jeden anderen nur die eigenen Zeilen.
 * Ein Fahrer, der diesen Hook aufriefe, bekäme also schlicht seine eigenen
 * Einträge — kein Datenleck, aber auch kein Sinn. Deshalb ist die Seite dahinter
 * nur über das Admin-Menü erreichbar.
 */
export function useAlleZeiten(vonISO: string) {
  return useQuery({
    queryKey: ["zeit-alle", vonISO],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("time_entries")
        // Der Fremdschlüssel MUSS benannt werden: Seit Migration 031 gibt es zwei
        // Wege von time_entries zu profiles (profile_id und storniert_von).
        // PostgREST weiß dann nicht, welchen es meint, wirft PGRST201 — und die
        // Seite bleibt still leer. Genau das ist passiert.
        .select("*, person:profiles!time_entries_profile_id_fkey(full_name, role)")
        .gte("started_at", vonISO)
        .order("started_at", { ascending: false })
        .limit(500);
      if (error) throw error;
      return (data ?? []) as unknown as TimeEntryMitPerson[];
    },
  });
}

/** Netto-Arbeitszeit eines Eintrags in Minuten (Pause bereits abgezogen). */
export function dauerMinuten(e: TimeEntry, jetzt = Date.now()) {
  const start = new Date(e.started_at).getTime();
  const ende = e.ended_at ? new Date(e.ended_at).getTime() : jetzt;
  return Math.max(0, Math.round((ende - start) / 60000) - e.break_minutes);
}

/** 495 → "8:15 h" */
export function formatDauer(min: number) {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h}:${String(m).padStart(2, "0")} h`;
}
